import TagaLogoColor from "@/assets/images/taganow-logo-color.svg";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { LIBRARY_URL } from "@/config/api";
import { PAGE_SIZE_DEFAULT } from "@/constants/app.constants";
import MenuSettingAccountPopup from "@/features/account/screens/MenuSettingAccountPopup";
import { getNotification } from "@/features/notification/api/notification.api";
import { NotificationStatus } from "@/features/notification/constants/notification.constant";
import NotificationComp from "@/features/notification/screens/Notification/component/Notification";
import { useAppSelector } from "@/redux/store";
import {
  Apps,
  BuildingInsights_3,
  Forum,
  Home,
  MediaLibrary,
  NotificationNew,
  ScisTransparentSupply,
  Task,
  UserMultiple,
  WorkflowAutomation,
} from "@carbon/icons-react";
import { memo, useEffect, useState } from "react";
import { NavLink } from "react-router-dom";

const navLinks = [
  {
    url: "/",
    end: false,
    icon: <Home size="20" />,
    label: "Trang chủ",
  },
  {
    url: "/group",
    end: false,
    icon: <BuildingInsights_3 size="20" />,
    label: "Nhóm",
    badge: "3",
  },
  {
    url: "/friends/list",
    end: false,
    icon: <UserMultiple size="20" />,
    label: "Kết nối",
    badge: "12",
  },
];

const workLinks = [
  {
    url: "/task?role=TODO",
    end: false,
    icon: <Task size="20" />,
    label: "Quản lý Công việc",
    des: "Tổ chức và theo dõi tiến độ công việc hiệu quả",
    color: "bg-blue-50 text-blue-600",
  },
  {
    url: "/operation",
    end: false,
    icon: <WorkflowAutomation size="20" />,
    label: "Quản lý Vận hành",
    des: "Tối ưu quy trình và tự động hóa công việc",
    color: "bg-green-50 text-green-600",
  },
  {
    url: "/cloud",
    end: false,
    icon: <MediaLibrary size="20" />,
    label: "Thư viện lưu trữ",
    des: "Lưu trữ và quản lý tài liệu an toàn",
    color: "bg-purple-50 text-purple-600",
  },
  {
    url: `${LIBRARY_URL}/studio`,
    end: true,
    icon: <ScisTransparentSupply size="20" />,
    label: "Taga Studiø",
    des: "Bộ công cụ sáng tạo và quản lý chuyên nghiệp",
    color: "bg-orange-50 text-orange-600",
  },
];

const Header = () => {
  const userInfo = useAppSelector((state) => state.auth.user);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [headerElementCount, setHeaderElementCount] = useState({
    notifications: 0,
    messages: 5,
  });

  useEffect(() => {
    getNotificationsUnread();
  }, []);

  const getNotificationsUnread = async () => {
    const result = await getNotification({
      pageNumber: 1,
      pageSize: PAGE_SIZE_DEFAULT,
      notificationStatusFilter: NotificationStatus.UNREAD,
    });
    setHeaderElementCount({
      ...headerElementCount,
      notifications: result.totalElements,
    });
  };

  return (
    <div className="fixed bg-white/95 w-full h-[70px] z-50 shadow-sm backdrop-blur-xl">
      <div className="flex justify-between items-center py-2 px-4 lg:px-6 w-full h-[70px]">
        {/* Logo */}
        <NavLink
          to="/"
          className="flex items-center space-x-3 min-w-fit"
        >
          <div>
            <img
              src={TagaLogoColor}
              alt="TAGA Logo"
              className="h-8 cursor-pointer 5"
            />
            <p className="text-xs text-gray-500 leading-none">
              Professional Network
            </p>
          </div>
        </NavLink>

        {/* Navigation Links */}
        <div className="hidden lg:flex gap-2">
          {navLinks.map((item, index) => {
            return (
              <NavLink
                key={index}
                to={item.url}
                className={({ isActive }) =>
                  `relative flex items-center gap-2 py-2 px-4 rounded font-medium transition-all duration-200 ${
                    isActive
                      ? "bg-gradient-to-r from-blue-50 to-purple-50 text-blue-700 "
                      : "text-gray-600 hover:bg-gray-50 hover:text-gray-900"
                  }`
                }
              >
                {item.icon}
                <span className="text-sm">{item.label}</span>
                {item.badge && (
                  <Badge
                    variant="secondary"
                    className="bg-red-100 text-red-600 text-xs h-5 min-w-5 flex items-center justify-center ml-1"
                  >
                    {item.badge}
                  </Badge>
                )}
              </NavLink>
            );
          })}
        </div>

        {/* Right Actions */}
        <div className="flex items-center gap-2">
          {/* Search Button - Mobile only */}

          {/* Apps Menu */}
          <Popover
            open={isMenuOpen}
            onOpenChange={setIsMenuOpen}
          >
            <PopoverTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                className="p-2 h-auto hover:bg-gray-100 rounded-full transition-colors"
              >
                <Apps
                  size="20"
                  className="text-gray-600"
                />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="p-0 border-none shadow-none w-fit h-fit mx-6">
              <div className="bg-white shadow-2xl rounded-xl border border-gray-100 w-[480px] overflow-hidden">
                <div className="p-6 border-b border-gray-100">
                  <h3 className="text-xl font-semibold text-gray-900 mb-1">
                    Workspace
                  </h3>
                  <p className="text-sm text-gray-500">
                    Truy cập nhanh các công cụ làm việc
                  </p>
                </div>

                <div className="p-4">
                  <div className="grid gap-3">
                    {workLinks.map((item, index) => {
                      return (
                        <NavLink
                          key={index}
                          to={item.url}
                          target={item.end ? "_blank" : ""}
                          className="flex gap-4 items-start p-3 rounded-xl hover:bg-gray-50 transition-colors group"
                          onClick={() => setIsMenuOpen(false)}
                        >
                          <div
                            className={`p-3 rounded-xl ${item.color} group-hover:scale-105 transition-transform`}
                          >
                            {item.icon}
                          </div>
                          <div className="flex-1">
                            <h4 className="font-semibold text-gray-900 mb-1 text-sm">
                              {item.label}
                            </h4>
                            <p className="text-xs text-gray-500 leading-relaxed">
                              {item.des}
                            </p>
                          </div>
                        </NavLink>
                      );
                    })}
                  </div>
                </div>

                <div className="p-4 bg-gradient-to-r from-blue-50 to-purple-50 border-t border-gray-100">
                  <p className="text-sm text-gray-600 text-center">
                    💼 Quản lý toàn diện công việc và kết nối chuyên nghiệp
                  </p>
                </div>
              </div>
            </PopoverContent>
          </Popover>

          {/* Notifications */}
          <Popover>
            <PopoverTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                className="relative p-2 h-auto hover:bg-gray-100 rounded-full transition-colors"
              >
                <NotificationNew
                  size="20"
                  className="text-gray-600"
                />
                {headerElementCount.notifications > 0 && (
                  <Badge className="absolute -top-1 -right-1 bg-red-500 text-white text-xs h-5 w-5 flex items-center justify-center p-0 border-2 border-white">
                    {headerElementCount.notifications}
                  </Badge>
                )}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="p-0 border-none shadow-none w-fit h-fit mx-6">
              <div className="bg-white shadow-2xl rounded-xl border border-gray-100 w-[380px] overflow-hidden">
                <div className="max-h-[calc(100vh-200px)] overflow-y-hidden">
                  <NotificationComp />
                </div>
                <div className="p-3 border-t border-gray-100 text-center">
                  <NavLink
                    to="/notification"
                    className="text-sm text-blue-600 hover:text-blue-700 font-medium"
                  >
                    Xem tất cả thông báo
                  </NavLink>
                </div>
              </div>
            </PopoverContent>
          </Popover>

          {/* Messages */}
          <Popover>
            <PopoverTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                className="relative p-2 h-auto hover:bg-gray-100 rounded-full transition-colors"
              >
                <Forum
                  size="20"
                  className="text-gray-600"
                />
                <Badge className="absolute -top-1 -right-1 bg-green-500 text-white text-xs h-5 w-5 flex items-center justify-center p-0 border-2 border-white">
                  {headerElementCount.messages}
                </Badge>
              </Button>
            </PopoverTrigger>
            <PopoverContent className="p-0 border-none shadow-none w-fit h-fit ml-6">
              <div className="bg-white shadow-2xl rounded-xl border border-gray-100 w-[320px] overflow-hidden">
                <div className="p-4 border-b border-gray-100">
                  <h3 className="font-semibold text-gray-900">Tin nhắn</h3>
                </div>
                <div className="p-4 h-80 flex items-center justify-center text-gray-500">
                  <div className="text-center">
                    <Forum
                      size="32"
                      className="mx-auto mb-2 text-gray-300"
                    />
                    <p className="text-sm">Chưa có tin nhắn mới</p>
                  </div>
                </div>
                <div className="p-3 border-t border-gray-100 text-center">
                  <NavLink
                    to="/chats"
                    className="text-sm text-blue-600 hover:text-blue-700 font-medium"
                  >
                    Mở ứng dụng chat
                  </NavLink>
                </div>
              </div>
            </PopoverContent>
          </Popover>

          {/* User Profile */}
          {userInfo && (
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="ghost"
                  className="p-1 h-auto hover:bg-gray-100 rounded-full transition-colors"
                >
                  <Avatar className="h-9 w-9 ring-2 ring-transparent hover:ring-blue-200 transition-all">
                    <AvatarImage src={userInfo.photo || ""} />
                    <AvatarFallback className="uppercase bg-primary text-white font-semibold text-sm">
                      {userInfo.email.slice(0, 2)}
                    </AvatarFallback>
                  </Avatar>
                </Button>
              </PopoverTrigger>
              <PopoverContent className="p-0 border-none shadow-none w-fit h-fit ml-6">
                <div className="bg-white shadow-2xl rounded-xl border border-gray-100 overflow-hidden">
                  <MenuSettingAccountPopup />
                </div>
              </PopoverContent>
            </Popover>
          )}
        </div>
      </div>
    </div>
  );
};

export default memo(Header);
